import { inject, Injectable } from '@angular/core';
import { Select, Store } from '@ngxs/store';
import {
  AdminChallengeContent,
  Challenge, ChallengeHistory,
  DailyChallengeContent,
  MonthlyChallengeContent
} from '../services/models/challenges.model';
import { forkJoin, Observable } from 'rxjs';
import {
  PostUserDailyChallenge,
  PostUserMonthlyChallenge,
  DeleteUserDailyChallenge,
  DeleteUserMonthlyChallenge,
  GetAdminChallenges,
  GetUserChallenges,
  PutUserDailyChallenge,
  PutUserMonthlyChallenge,
  GetChallengesHistory,
  SetInitialValue,
} from './challenges.actions';
import { ChallengesSelectors } from './challenges.selectors';

@Injectable({
  providedIn: 'root'
})
export class ChallengesFacade {
  private readonly store = inject(Store);

  @Select(ChallengesSelectors.challenges.global) globalChallenge$: Observable<
    Challenge<AdminChallengeContent> | undefined
  >;
  @Select(ChallengesSelectors.challenges.assigned) assignedChallenge$: Observable<
    Challenge<AdminChallengeContent> | undefined
  >;
  @Select(ChallengesSelectors.challenges.daily) dailyChallenge$: Observable<
    Challenge<DailyChallengeContent> | undefined
  >;
  @Select(ChallengesSelectors.challenges.monthly) monthlyChallenge$: Observable<
    Challenge<MonthlyChallengeContent> | undefined
  >;
  @Select(ChallengesSelectors.history) historyChallenges$: Observable<
    ChallengeHistory | undefined
  >;
  @Select(ChallengesSelectors.isLoading) isLoading$!: Observable<boolean>;
  @Select(ChallengesSelectors.initialValue) initialValue$!: Observable<number>;

  get dailyChallenge(): Challenge<DailyChallengeContent> {
    return this.store.selectSnapshot(ChallengesSelectors.challenges.daily);
  }

  get monthlyChallenge(): Challenge<MonthlyChallengeContent> {
    return this.store.selectSnapshot(ChallengesSelectors.challenges.monthly);
  }

  get initialValue(): number {
    return this.store.selectSnapshot(ChallengesSelectors.initialValue);
  }

  getAdminChallenges() {
    return this.store.dispatch(new GetAdminChallenges());
  }

  getUserChallenges() {
    return this.store.dispatch(new GetUserChallenges());
  }

  getAllChallenges() {
    return forkJoin([
      this.store.dispatch(new GetAdminChallenges()),
      this.store.dispatch(new GetUserChallenges()),
    ]);
  }

  postUserDailyChallenge(goal: number) {
    return this.store.dispatch(new PostUserDailyChallenge({ goal }));
  }

  postUserMonthlyChallenge(goal: number) {
    return this.store.dispatch(new PostUserMonthlyChallenge({ goal }));
  }

  putUserDailyChallenge(goal: number) {
    return this.store.dispatch(new PutUserDailyChallenge({ goal }));
  }

  putUserMonthlyChallenge(goal: number) {
    return this.store.dispatch(new PutUserMonthlyChallenge({ goal }));
  }

  deleteUserDailyChallenge() {
    return this.store.dispatch(new DeleteUserDailyChallenge());
  }

  deleteUserMonthlyChallenge() {
    return this.store.dispatch(new DeleteUserMonthlyChallenge());
  }

  getChallengesHistory(currentPage = 1, pageSize = 10) {
    return this.store.dispatch(new GetChallengesHistory(pageSize, currentPage));
  }

  setInitialValue(initialValue: number) {
    return this.store.dispatch(new SetInitialValue(initialValue));
  }
}
